import { useState, useEffect } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Scale, RefreshCw, Info } from 'lucide-react';
import { diagnosticsAPI, organizationsAPI } from '../lib/api';

/**
 * P_UNIFIED_VALUE_ENGINE_1.0 Phase 5 — Reconciliation View.
 *
 * Pure server orchestration. The handler at GET
 * /admin/diagnostics/reconciliation delegates every $ to
 * services.value_engine.compute_value. This page calls that endpoint
 * and renders the response. No formulas live in this file.
 */

const formatMoney = (n) => (typeof n === 'number'
    ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—');

const statusBadge = (status, t) => {
    if (status === 'PASS') {
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="recon-status-badge">{t('reconciliation.status.pass')}</Badge>;
    }
    if (status === 'WARNING') {
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid="recon-status-badge">{t('reconciliation.status.warning')}</Badge>;
    }
    return <Badge className="bg-rose-100 text-rose-700 border-rose-200" data-testid="recon-status-badge">{t('reconciliation.status.investigate')}</Badge>;
};

const surfaceLabel = (key, t) => ({
    roi_dashboard:         t('reconciliation.surfaces.roi'),
    welcome_morning_brief: t('reconciliation.surfaces.welcome'),
    weekly_report_live:    t('reconciliation.surfaces.weeklyLive'),
    customer_visible_only: t('reconciliation.surfaces.customerVisible'),
    unified_engine:        t('reconciliation.surfaces.engine'),
})[key] || key;

const todayISO = () => new Date().toISOString().slice(0, 10);
const isoMinusDays = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
};

const AdminReconciliation = () => {
    const { t } = useTranslation();
    const [orgs, setOrgs] = useState([]);
    const [orgId, setOrgId] = useState('');
    const [start, setStart] = useState(isoMinusDays(7));
    const [end, setEnd] = useState(todayISO());
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        organizationsAPI.list().then(r => {
            const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
            setOrgs(sortByName(items));
            if (items.length && !orgId) setOrgId(items[0].id);
        }).catch(e => setError(e.message));
    }, []);

    const run = async () => {
        if (!orgId) return;
        setLoading(true);
        setError(null);
        try {
            const r = await diagnosticsAPI.reconciliation(orgId, start, end);
            setReport(r.data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'load_failed');
            setReport(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8" data-testid="admin-reconciliation-page">
            <header className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-semibold text-slate-900" data-testid="reconciliation-page-title">
                        {t('reconciliation.title')}
                    </h1>
                    <p className="text-sm text-slate-600 mt-1 max-w-2xl">{t('reconciliation.subtitle')}</p>
                </div>
            </header>

            <Card className="p-6 space-y-4" data-testid="card-reconciliation-form">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                        <Label htmlFor="org">{t('reconciliation.form.organization')}</Label>
                        <Select value={orgId} onValueChange={setOrgId}>
                            <SelectTrigger id="org" data-testid="recon-org-select"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                                {orgs.map(o => (
                                    <SelectItem key={o.id} value={o.id} data-testid={`recon-org-option-${o.id}`}>{o.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="start">{t('reconciliation.form.start')}</Label>
                        <Input id="start" type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="recon-start" />
                    </div>
                    <div>
                        <Label htmlFor="end">{t('reconciliation.form.end')}</Label>
                        <Input id="end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="recon-end" />
                    </div>
                </div>
                <div className="flex justify-end">
                    <Button onClick={run} disabled={loading || !orgId} data-testid="recon-run-btn">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        {t('reconciliation.form.run')}
                    </Button>
                </div>
            </Card>

            {error ? (
                <Card className="p-6 border-rose-200 bg-rose-50 text-rose-700" data-testid="reconciliation-error">
                    {t('reconciliation.error')}: {String(error)}
                </Card>
            ) : null}

            {report ? (
                <Card className="p-6 space-y-4" data-testid="card-reconciliation-result">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Scale className="w-5 h-5 text-slate-500" />
                            <h2 className="text-lg font-semibold text-slate-900">{t('reconciliation.result.title')}</h2>
                        </div>
                        {statusBadge(report.status, t)}
                    </div>
                    <p className="text-xs text-slate-500" data-testid="recon-status-reason">
                        {t(`reconciliation.statusReason.${report.status_reason}`, report.status_reason)}
                    </p>

                    <table className="w-full text-sm" data-testid="recon-table">
                        <thead>
                            <tr className="border-b border-slate-200 text-slate-500 uppercase text-xs">
                                <th className="text-left py-2 font-medium">{t('reconciliation.table.surface')}</th>
                                <th className="text-right py-2 font-medium">{t('reconciliation.table.value')}</th>
                                <th className="text-right py-2 font-medium">{t('reconciliation.table.taps')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {['roi_dashboard', 'welcome_morning_brief', 'weekly_report_live', 'customer_visible_only', 'unified_engine'].map(k => (
                                <tr key={k} className="border-b border-slate-100" data-testid={`recon-row-${k}`}>
                                    <td className="py-2 text-slate-700">{surfaceLabel(k, t)}</td>
                                    <td className="py-2 text-right text-slate-900 font-medium">{formatMoney(report.surfaces[k]?.value)}</td>
                                    <td className="py-2 text-right text-slate-500">{report.surfaces[k]?.taps ?? '—'}</td>
                                </tr>
                            ))}
                            <tr data-testid="recon-row-snapshot">
                                <td className="py-2 text-slate-700">
                                    {t('reconciliation.surfaces.snapshot')}
                                    {report.surfaces.snapshot?.snapshot_id ? (
                                        <span className="text-xs text-slate-400 ml-2">
                                            ({report.surfaces.snapshot.window?.week_start} → {report.surfaces.snapshot.window?.week_end})
                                        </span>
                                    ) : null}
                                </td>
                                <td className="py-2 text-right text-slate-900 font-medium">{report.surfaces.snapshot?.value !== null && report.surfaces.snapshot?.value !== undefined ? formatMoney(report.surfaces.snapshot.value) : '—'}</td>
                                <td className="py-2 text-right text-slate-500">—</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Phase 5 Section 4 — variance explainer. Informational
                        only, no warning styling. */}
                    {report.status === 'WARNING' && report.surfaces.snapshot?.value !== null ? (
                        <Card className="p-4 bg-slate-50 border-slate-200 text-slate-700" data-testid="recon-variance-explainer">
                            <div className="flex gap-2">
                                <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                                <div className="text-sm space-y-1">
                                    <div className="font-medium">{t('reconciliation.variance.title')}</div>
                                    <p>{t('reconciliation.variance.body')}</p>
                                    <ul className="list-disc list-inside text-slate-600">
                                        <li>{t('reconciliation.variance.pricingModel')}</li>
                                        <li>{t('reconciliation.variance.orphanRepair')}</li>
                                        <li>{t('reconciliation.variance.historicalValues')}</li>
                                    </ul>
                                </div>
                            </div>
                        </Card>
                    ) : null}

                    {/* Phase 5 Section 3 — Engine info readout */}
                    <div className="border-t border-slate-100 pt-3 text-xs text-slate-500 space-y-1" data-testid="recon-engine-info">
                        <div>{t('reconciliation.engine.engineVersion')}: v{report.engine_version}</div>
                        <div>{t('reconciliation.engine.pricingSource')}: {report.surfaces.snapshot?.pricing_source || t('reconciliation.engine.pricingSourceNone')}</div>
                        <div className="font-mono text-[11px]">
                            {Object.entries(report.surfaces.unified_engine?.platform_values_used || {}).map(([k, v]) =>
                                `${k}=$${v}`
                            ).join('  ·  ')}
                        </div>
                    </div>
                </Card>
            ) : null}
        </div>
    );
};

export default AdminReconciliation;
